/**
 * Canvas-based annotation tools for the feedback widget.
 * Creates an overlay canvas on top of a screenshot for drawing.
 */

interface Annotation {
  type: "arrow" | "rectangle" | "text" | "highlight";
  x: number;
  y: number;
  width?: number;
  height?: number;
  endX?: number;
  endY?: number;
  color?: string;
  text?: string;
}

export function createAnnotationUI(
  imageCanvas: HTMLCanvasElement,
  onDone: (annotations: Annotation[], annotatedCanvas: HTMLCanvasElement) => void
) {
  const annotations: Annotation[] = [];
  let currentTool: Annotation["type"] = "rectangle";
  let drawing = false;
  let startX = 0;
  let startY = 0;

  const container = document.createElement("div");
  container.style.cssText = `
    position: fixed; inset: 0; z-index: 100001;
    background: rgba(0,0,0,0.8);
    display: flex; flex-direction: column;
  `;

  // Toolbar
  const toolbar = document.createElement("div");
  toolbar.style.cssText = `
    display: flex; gap: 8px; padding: 12px;
    justify-content: center; background: #222;
  `;

  const tools: { label: string; type: Annotation["type"] }[] = [
    { label: "▭ Rectangle", type: "rectangle" },
    { label: "→ Arrow", type: "arrow" },
    { label: "█ Highlight", type: "highlight" },
  ];

  tools.forEach((tool) => {
    const btn = document.createElement("button");
    btn.textContent = tool.label;
    btn.style.cssText = `
      padding: 6px 14px; border: 1px solid #555; border-radius: 6px;
      background: ${tool.type === currentTool ? "#4c6ef5" : "#333"};
      color: white; cursor: pointer; font-size: 12px;
    `;
    btn.addEventListener("click", () => {
      currentTool = tool.type;
      toolbar.querySelectorAll("button").forEach((b, i) => {
        if (i < tools.length) {
          (b as HTMLElement).style.background =
            tools[i].type === currentTool ? "#4c6ef5" : "#333";
        }
      });
    });
    toolbar.appendChild(btn);
  });

  const doneBtn = document.createElement("button");
  doneBtn.textContent = "Done ✓";
  doneBtn.style.cssText = `
    padding: 6px 18px; border: none; border-radius: 6px;
    background: #22c55e; color: white; cursor: pointer;
    font-size: 12px; font-weight: 600; margin-left: 16px;
  `;
  doneBtn.addEventListener("click", () => {
    container.remove();
    onDone(annotations, canvas);
  });
  toolbar.appendChild(doneBtn);

  container.appendChild(toolbar);

  // Scrollable canvas wrapper
  const canvasWrap = document.createElement("div");
  canvasWrap.style.cssText = `
    flex: 1; overflow: auto; display: flex;
    align-items: flex-start; justify-content: center;
  `;

  const canvas = document.createElement("canvas");
  canvas.width = imageCanvas.width;
  canvas.height = imageCanvas.height;
  canvas.style.cssText = `
    cursor: crosshair; max-width: 100%;
  `;

  const ctx = canvas.getContext("2d")!;

  function redraw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imageCanvas, 0, 0);

    annotations.forEach((ann) => {
      ctx.strokeStyle = ann.color || "#ef4444";
      ctx.lineWidth = 3;

      switch (ann.type) {
        case "rectangle":
          ctx.strokeRect(ann.x, ann.y, ann.width || 0, ann.height || 0);
          break;
        case "arrow":
          ctx.beginPath();
          ctx.moveTo(ann.x, ann.y);
          ctx.lineTo(ann.endX || ann.x, ann.endY || ann.y);
          ctx.stroke();
          break;
        case "highlight":
          ctx.fillStyle = (ann.color || "#facc15") + "44";
          ctx.fillRect(ann.x, ann.y, ann.width || 0, ann.height || 0);
          break;
      }
    });
  }

  redraw();

  canvas.addEventListener("mousedown", (e) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    startX = (e.clientX - rect.left) * scaleX;
    startY = (e.clientY - rect.top) * scaleY;
    drawing = true;
  });

  canvas.addEventListener("mousemove", (e) => {
    if (!drawing) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const currentX = (e.clientX - rect.left) * scaleX;
    const currentY = (e.clientY - rect.top) * scaleY;

    redraw();
    ctx.strokeStyle = "#ef4444";
    ctx.lineWidth = 3;

    switch (currentTool) {
      case "rectangle":
        ctx.strokeRect(startX, startY, currentX - startX, currentY - startY);
        break;
      case "arrow":
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(currentX, currentY);
        ctx.stroke();
        break;
      case "highlight":
        ctx.fillStyle = "#facc1544";
        ctx.fillRect(startX, startY, currentX - startX, currentY - startY);
        break;
    }
  });

  canvas.addEventListener("mouseup", (e) => {
    if (!drawing) return;
    drawing = false;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const endX = (e.clientX - rect.left) * scaleX;
    const endY = (e.clientY - rect.top) * scaleY;

    const annotation: Annotation = {
      type: currentTool,
      x: startX,
      y: startY,
      color: currentTool === "highlight" ? "#facc15" : "#ef4444",
    };

    if (currentTool === "arrow") {
      annotation.endX = endX;
      annotation.endY = endY;
    } else {
      annotation.width = endX - startX;
      annotation.height = endY - startY;
    }

    annotations.push(annotation);
    redraw();
  });

  canvasWrap.appendChild(canvas);
  container.appendChild(canvasWrap);
  document.body.appendChild(container);
}
